// Reusable per-campaign action buttons. Originally inlined in
// MyCampaignsPage; extracted so AdvertiserCampaignDetailPage (and any future
// per-campaign surface) can use them without importing from a page module.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, BarChart3 } from 'lucide-react';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { estimateServiceFee, estimateTotalWithServiceFee } from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { createIdempotencyKey } from '../../lib/idempotency';
import { formatMoneySmart } from './shared/format';
import StripePaymentSection from './StripePaymentSection';
import DateField from './steps/DateField';
import { tintFromAccent } from './steps/_shared';
import { buildFollowUpCampaignName, buildFollowUpDates, cancelCopyFor } from './campaignSharedHelpers';
import { CancelCampaignModal } from './CancelCampaignModal';
import { EditCampaignModal } from './EditCampaignModal';
import { rerunGeoFields } from './rerunGeoFields';
import PrimaryButton from './shared/PrimaryButton';
import Banner from './shared/Banner';
import InventoryWarningCard from './InventoryWarningCard';
import { useExtendFeasibility } from '../../services/useExtendFeasibility';
import { useCreativePreviewUrl } from './shared/useCreativePreviewUrl';

type SubmissionRow = SelfServiceSubmissionRow;

// "Run this Campaign Again" pill on a campaign card. Pre-fills the same
// publisher's wizard with this Source campaign's fields (business info,
// product, markets, creative, click-through, name, budget, dates) so the
// advertiser can relaunch with one tweak. Name + dates are derived via the
// shared follow-up helpers; payment plan is forced to upfront downstream in
// the wizard regardless of source.
export function RerunMenu({
  submission, cfg,
}: {
  submission: SubmissionRow;
  cfg: PublisherConfig;
}) {
  const navigate = useNavigate();
  const accent = cfg.accentColor || cfg.primaryColor;

  // CTV-457: when the source campaign had multiple creatives, all of them
  // travel through to the new submission. The wizard hydrates slot 0 from
  // the first entry and the scheduler from the rest.
  const liveCreatives = (submission.creatives ?? []).filter((c) => c.status === 'active');
  const liveCreative = liveCreatives[0] ?? null;
  const followUpDates = buildFollowUpDates(
    submission.campaign?.startDate,
    submission.campaign?.endDate,
  );
  const followUpName = buildFollowUpCampaignName(
    submission.campaign?.campaignName ?? '',
    followUpDates.startDate,
  );

  const samePublisherPayload = {
    sourceSubmissionId: submission.id,
    formData: {
      businessName: submission.business_info?.businessName ?? '',
      businessWebsite: submission.business_info?.businessWebsite ?? '',
      // CTV-592: contact fields (name/phone/email) follow the current submitter,
      // never the source campaign, see CONTEXT.md. Omitted here so the wizard
      // seeds them from `useAuth().user` alone.
      campaignName: followUpName,
      budget: submission.campaign?.budget ?? null,
      startDate: followUpDates.startDate,
      endDate: followUpDates.endDate,
      selectedProduct: submission.campaign?.selectedProduct ?? '',
      targetMarkets: submission.campaign?.targetMarkets ?? [],
      ...rerunGeoFields(submission.campaign?.targetMarkets),
    },
    creatives: liveCreatives.map((c) => ({
      storagePath: c.storage_path,
      filename: c.filename,
      mimeType: c.mime_type ?? 'video/mp4',
      sizeBytes: c.size_bytes ?? 0,
      clickThroughUrl: c.click_through_url,
      // CTV-476: shares are tied to specific dates; clearing the start/end
      // here means the wizard will default to single-100% segment shares on
      // the new window.
      startDate: undefined,
      endDate: undefined,
    })),
    // Back-compat: pre-CTV-457 wizard versions read creativeMeta directly.
    // Synthesized from the primary creative when present.
    creativeMeta: liveCreative
      ? {
          storagePath: liveCreative.storage_path,
          filename: liveCreative.filename,
          mimeType: liveCreative.mime_type ?? 'video/mp4',
          sizeBytes: liveCreative.size_bytes ?? 0,
        }
      : undefined,
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        analytics.track(EVENTS.CAMPAIGN_RERUN_STARTED, { station_slug: cfg.slug });
        navigate(`/${cfg.slug}/start/campaign`, { state: { rerunFrom: samePublisherPayload } });
      }}
      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border hover:brightness-105 transition-all"
      style={{
        color: accent,
        borderColor: `${accent}40`,
        backgroundColor: `${accent}0f`,
      }}
    >
      Run this Campaign Again
    </button>
  );
}

// Archived-campaign pill — pre-fills everything from the source submission
// and routes the user back into the wizard with the campaign hydrated.
//
// CTV-466 follow-up: for the rejection case we now carry the full creative
// set (active + pending_review + voided) into the wizard so the advertiser
// can see exactly which files the publisher rejected. The wizard's
// commercial step renders a red Rejected band + reviewer notes per voided
// slot and hard-gates Next until each rejection is resolved. Submitting
// routes through POST /submissions/:id/resubmit (via the `isResubmit` flag)
// so the existing creative_rejected row is rebuilt in place rather than
// spawning a new one in the publisher's inbox.
export function FixAndResubmitButton({ submission, cfg }: { submission: SubmissionRow; cfg: PublisherConfig }) {
  const navigate = useNavigate();
  const accent = cfg.accentColor || cfg.primaryColor;
  const wasRejected = submission.status === 'creative_rejected';
  const label = wasRejected ? 'Fix & resubmit' : 'Try again';

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // CTV-466 follow-up: carry every reviewable creative back into the
    // wizard, regardless of status. Voided rows surface as "Rejected" slots
    // with reviewer notes; pending_review + active rows surface as ready-to-go
    // slots the advertiser can keep, replace, or remove. Retired rows are
    // historical and stay hidden.
    const carriedCreatives = (submission.creatives ?? []).filter(
      (c) => c.status === 'active' || c.status === 'pending_review' || c.status === 'voided',
    );
    const liveCreative = carriedCreatives.find((c) => c.status !== 'voided') ?? carriedCreatives[0] ?? null;
    const payload = {
      formData: {
        businessName: submission.business_info?.businessName ?? '',
        businessWebsite: submission.business_info?.businessWebsite ?? '',
        // CTV-592: contact fields (name/phone/email) follow the current submitter,
        // never the source campaign, see CONTEXT.md. Omitted here so the wizard
        // seeds them from `useAuth().user` alone.
        campaignName: submission.campaign?.campaignName ?? '',
        budget: submission.campaign?.budget ?? null,
        selectedProduct: submission.campaign?.selectedProduct ?? '',
        startDate: submission.campaign?.startDate ?? '',
        endDate: submission.campaign?.endDate ?? '',
        targetMarkets: submission.campaign?.targetMarkets ?? [],
        // Sniff target_geographies format to restore the right tab — the
        // bare targetMarkets field isn't enough on its own: without
        // targetingMode + geoMode + the per-tab selection arrays, the new
        // wizard opens with the wrong tab and no chips.
        ...rerunGeoFields(submission.campaign?.targetMarkets),
      },
      creatives: carriedCreatives.map((c) => ({
        storagePath: c.storage_path,
        filename: c.filename,
        mimeType: c.mime_type ?? 'video/mp4',
        sizeBytes: c.size_bytes ?? 0,
        clickThroughUrl: c.click_through_url,
        // CTV-466 follow-up + CTV-476: carry each creative's stored schedule
        // window AND per-segment shares so the wizard's Scheduler reproduces
        // exactly what the buyer originally set.
        startDate: c.creative_start_date,
        endDate: c.creative_end_date,
        segmentShares: (c.segment_shares ?? []).map((s) => ({
          startDate: s.start_date,
          endDate: s.end_date,
          share: s.share,
        })),
        sourceCreativeId: c.id,
        rejected: c.status === 'voided',
        rejectionReason: c.rejection_reason ?? null,
        rejectionReasonCodes: c.rejection_reason_codes ?? null,
      })),
      creativeMeta: liveCreative
        ? {
            storagePath: liveCreative.storage_path,
            filename: liveCreative.filename,
            mimeType: liveCreative.mime_type ?? 'video/mp4',
            sizeBytes: liveCreative.size_bytes ?? 0,
          }
        : undefined,
      sourceSubmissionId: submission.id,
      // CTV-466 follow-up: route the wizard's submit to /resubmit when this
      // handoff is rejection-driven so the rejected row is rebuilt in place.
      isResubmit: wasRejected,
    };
    // CTV-466 follow-up: when any creative was actually rejected, land on
    // the Commercial step so the advertiser sees the rejection banner +
    // per-slot reviewer notes immediately. The "Try again" / archived case
    // (no voided creatives, e.g. payment-failed re-runs) keeps landing on
    // Campaign Setup since those flows benefit from a re-review of budget
    // and dates before the creative step.
    const hasRejectedCreative = carriedCreatives.some((c) => c.status === 'voided');
    const landingSlug = hasRejectedCreative ? 'video' : 'campaign';
    navigate(`/${cfg.slug}/start/${landingSlug}`, { state: { rerunFrom: payload } });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full text-white transition-all hover:brightness-110 active:scale-[0.98]"
      style={{
        background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
      }}
    >
      {label}
      <span aria-hidden>→</span>
    </button>
  );
}

// CTV-445: jump straight from a My Campaigns card to this campaign's delivery
// report, skipping the detail page. Surfaced on active + completed (delivered)
// campaigns — the ones that actually have delivery data to report. Stops click
// propagation so the surrounding card's navigate-to-detail doesn't also fire.
export function ViewReportButton({ submission, cfg }: { submission: SubmissionRow; cfg: PublisherConfig }) {
  const navigate = useNavigate();
  const accent = cfg.accentColor || cfg.primaryColor;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/${cfg.slug}/my-campaigns/${submission.id}/report`);
      }}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-3 py-1.5 rounded-full text-white transition-all hover:brightness-110 active:scale-[0.98]"
      style={{
        background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
        boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
      }}
    >
      <BarChart3 className="w-3.5 h-3.5" aria-hidden />
      View report
    </button>
  );
}

// CTV-466 follow-up: voluntary archive of a creative_rejected submission.
// Greyed-out style sits alongside FixAndResubmitButton on the My Campaigns
// card for the rejected state. The confirmation modal is intentionally
// minimal — once archived the row drops out of Pending and lands in Archived.
export function ArchiveButton({
  submission,
  cfg,
  onArchived,
}: {
  submission: SubmissionRow;
  cfg: PublisherConfig;
  onArchived: (id: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const accent = cfg.accentColor || cfg.primaryColor;

  if (submission.status !== 'creative_rejected') return null;

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${submission.id}/archive`, {});
      setOpen(false);
      onArchived(submission.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive submission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-all"
      >
        Archive
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
            onClick={(e) => {
              e.stopPropagation();
              if (!busy) setOpen(false);
            }}
          />
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-md bg-white rounded-2xl border border-slate-200/80 p-5"
            style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 leading-tight mb-1">
              Archive this campaign?
            </h2>
            <p className="text-[12.5px] text-slate-600 mb-4">
              You'll be archiving <strong>{submission.campaign?.campaignName || 'this campaign'}</strong> without fixing the rejected commercials. It won't run, and you'll need to start over from scratch if you want to try again later. Are you sure?
            </p>
            {error && (
              <div className="mb-4">
                <Banner tone="warn" label="Couldn't archive">{error}</Banner>
              </div>
            )}
            {/* CTV-466 follow-up: per the self-serve button standard, when the
                safe option is the obvious primary CTA, the destructive verb
                lives on the tertiary Pattern B text — so "Keep it" is the
                station-themed pill and "Yes, archive" is the slate dismiss. */}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Yes, archive
              </button>
              <PrimaryButton
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                accentColor={accent}
                primaryColor={cfg.primaryColor}
              >
                Keep it
              </PrimaryButton>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Standalone "Extend campaign" pill — sits next to RerunMenu in the card footer
// when the campaign is still captured (active or scheduled).
// Cancel-campaign pill. Visible only when canCancel(submission) is true. The
// per-status modal copy variant is computed via cancelCopyFor() and handed to
// CancelCampaignModal so the wording matches what the backend will actually do.
export function CancelButton({
  submission,
  cfg,
  onCanceled,
}: {
  submission: SubmissionRow;
  cfg: PublisherConfig;
  onCanceled: (id: string, canceledAt: string) => void;
}) {
  // Track the variant the user opened the modal with so it survives a
  // post-success status change on the submission. We also defer notifying
  // the parent until the modal actually closes, because the list page filters
  // rows by bucket — flipping status to 'canceled' immediately would move
  // the row out of the current bucket, unmounting this component and the
  // success view inside it.
  const [openVariant, setOpenVariant] = React.useState<ReturnType<typeof cancelCopyFor>>(null);
  const [pendingResult, setPendingResult] = React.useState<{ id: string; canceled_at: string } | null>(null);
  const currentVariant = cancelCopyFor(submission);
  if (!currentVariant && !openVariant) return null;

  const handleClose = () => {
    setOpenVariant(null);
    if (pendingResult) {
      onCanceled(pendingResult.id, pendingResult.canceled_at);
      setPendingResult(null);
    }
  };

  return (
    <>
      {currentVariant && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenVariant(currentVariant);
          }}
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
          Cancel
        </button>
      )}
      {openVariant && (
        <CancelCampaignModal
          submission={submission}
          cfg={cfg}
          variant={openVariant}
          onClose={handleClose}
          onSuccess={(updated) => {
            // Stash the result so we can forward it to the parent when the
            // user dismisses the modal. Updating the parent now would unmount
            // this component (the row leaves the current bucket).
            setPendingResult({ id: updated.id, canceled_at: updated.canceled_at });
          }}
        />
      )}
    </>
  );
}

// CTV-440: combined "Edit campaign" pill. Replaces ExtendButton + CancelButton
// on currently-delivering campaigns. Opens a large EditCampaignModal with
// Extend / Swap creative / Cancel tabs.
export function EditButton({
  submission, cfg, onExtended, onCanceled, onCreativeSwapped, defaultTab,
}: {
  submission: SubmissionRow;
  cfg: PublisherConfig;
  onExtended: (id: string, newEndDate: string, additionalBudget: number) => void;
  onCanceled: (id: string, canceledAt: string) => void;
  onCreativeSwapped: () => void;
  defaultTab?: 'extend' | 'swap' | 'cancel';
}) {
  const [open, setOpen] = React.useState(false);
  const accent = cfg.accentColor || cfg.primaryColor;
  if (!submission.campaign?.endDate) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          analytics.track(EVENTS.EDIT_CAMPAIGN_OPENED, {
            station_slug: cfg.slug,
            submission_id: submission.id,
            tab: defaultTab ?? 'extend',
          });
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border hover:brightness-105 transition-all"
        style={{
          color: accent,
          borderColor: `${accent}40`,
          backgroundColor: `${accent}0f`,
        }}
      >
        Edit campaign
      </button>
      {open && (
        <EditCampaignModal
          submission={submission}
          cfg={cfg}
          defaultTab={defaultTab}
          onClose={() => setOpen(false)}
          onExtended={onExtended}
          onCanceled={onCanceled}
          onCreativeSwapped={onCreativeSwapped}
        />
      )}
    </>
  );
}

// CTV-488 removed `CreativeScheduleUpdate`, `ExtendCreativeReviewStep`, and
// their support helpers — the Extend flow no longer asks the buyer to re-
// schedule creatives. The server applies the Blanket extension rule instead;
// see `docs/adr/0008-blanket-extension-creative-rule.md`.

interface ExtendPanelProps {
  submission: SubmissionRow;
  cfg: PublisherConfig;
  currentEndDate: string;
  // Called when the user dismisses the panel (e.g. the parent modal's close
  // button or the Cancel CTA). Required so the panel can disable form
  // controls while busy and emit a clear "I'm done" signal.
  onClose: () => void;
  onExtended: (newEndDate: string, additionalBudget: number) => void;
}

// CTV-488 + CTV-504: per-installment row returned by `/submissions/:id/extend-
// payment-intent` for monthly extensions. Server-side `buildMonthlyExtension`
// is the source of truth; the receipt step renders this list verbatim.
interface MonthlyInstallmentPreview {
  installmentNumber: number;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  amountCents: number;
  serviceFeeCents: number;
}

// CTV-504: when the extension lands inside the in-progress anniversary bucket
// (or partially crosses it), the backend reports the in-place flight update so
// the receipt can show a "today" line tied to the leading-partial portion.
// `installmentBumped` is populated when the in-progress installment was still
// `scheduled` (no on-session charge today, but its amount_cents bumps so the
// next cron charge will be larger — the receipt surfaces both old and new
// amounts so the buyer isn't surprised on the due date).
interface InPlacePreview {
  newEndDate: string;
  pacingBudgetDeltaCents: number;
  installmentBumped: {
    dueDate: string;
    periodStart: string;
    periodEnd: string;
    amountCentsBefore: number;
    amountCentsAfter: number;
  } | null;
}

/**
 * CTV-440 + CTV-488: overlay-less extend form. Rendered inline by the Extend
 * tab of EditCampaignModal. The parent supplies the chrome (modal box, close
 * button); this component is just the form + Stripe handoff.
 *
 * Two steps:
 *   1. **Inputs** — new end date + additional budget, gated by live inventory
 *      feasibility (`useExtendFeasibility`).
 *   2. **Extension receipt** — charge summary (upfront: total today; monthly:
 *      per-installment breakdown from the prep response), the read-only list
 *      of **Last running creatives** that will continue through the extended
 *      tail (with their tail-segment Weight), a swap hint pointing to "Run
 *      this Campaign Again," and the confirm action (Stripe Elements for
 *      upfront / single Confirm button for monthly).
 *
 * The wizard's per-extend creative-scheduling step was removed in CTV-488 —
 * the server applies the Blanket extension rule to the Last running
 * creatives automatically. See `docs/adr/0008-blanket-extension-creative-rule.md`.
 */
export function ExtendPanel({ submission, cfg, currentEndDate, onClose, onExtended }: ExtendPanelProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  // Earliest valid extension is the day after the current end date.
  const minNewEnd = React.useMemo(() => {
    const next = new Date(currentEndDate.slice(0, 10) + 'T00:00:00');
    next.setDate(next.getDate() + 1);
    const y = next.getFullYear();
    const m = String(next.getMonth() + 1).padStart(2, '0');
    const d = String(next.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [currentEndDate]);
  const [newEnd, setNewEnd] = React.useState(minNewEnd);
  const [additionalBudget, setAdditionalBudget] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // CTV-488 + CTV-504: two-step machine — inputs → receipt. Receipt holds the
  // shared prep response: a Stripe clientSecret when there's an on-session
  // charge (mounted in StripePaymentSection), and for monthly the per-
  // installment list. When the monthly extension is purely in-place against an
  // unpaid tail installment, clientSecret is null and the buyer confirms with
  // a single Confirm button (no Elements).
  const [step, setStep] = React.useState<'inputs' | 'receipt'>('inputs');
  const [paymentClientSecret, setPaymentClientSecret] = React.useState<string | null>(null);
  const [paymentAmountCents, setPaymentAmountCents] = React.useState<number>(0);
  const [paymentIntentId, setPaymentIntentId] = React.useState<string | null>(null);
  const [monthlyPreview, setMonthlyPreview] = React.useState<MonthlyInstallmentPreview[] | null>(null);
  const [inPlacePreview, setInPlacePreview] = React.useState<InPlacePreview | null>(null);

  const product = submission.campaign?.selectedProduct
    ? cfg.products.find((p) => p.id === submission.campaign?.selectedProduct)
    : undefined;
  const cpm = product?.cpm ?? 0;
  const additionalImpressions = additionalBudget && additionalBudget > 0
    ? Math.round((additionalBudget / cpm) * 1000)
    : 0;
  // CTV-518: service-fee gross-up estimate on the additional budget. The
  // backend's PI amount is authoritative — the receipt step renders
  // paymentAmountCents from the prep response wherever a charge exists.
  const additionalFee = additionalBudget && additionalBudget > 0
    ? estimateServiceFee(additionalBudget, cfg.serviceFeeRates)
    : 0;
  const additionalTotal = additionalBudget && additionalBudget > 0
    ? estimateTotalWithServiceFee(additionalBudget, cfg.serviceFeeRates)
    : 0;

  // CTV-473 + CTV-474: live inventory check scoped to the NEW flights added
  // by this extension. The hook passes `extendFromDate = currentEndDate + 1
  // day` and the additional budget; the backend filters periods to only the
  // new ones. The submission's own line item is excluded so the extension
  // doesn't count against itself. When verdict.feasible is false we hard-block
  // Continue. CTV-474: the warning card surfaces an extension-date suggestion
  // rather than the prior "max additional budget" copy.
  const isMonthly = submission.payment_plan === 'monthly';
  const feasibility = useExtendFeasibility({
    submissionId: submission.id,
    productId: submission.campaign?.selectedProduct,
    startDate: submission.campaign?.startDate,
    currentEndDate,
    newEndDate: newEnd && newEnd > currentEndDate ? newEnd : undefined,
    additionalBudget: additionalBudget && additionalBudget > 0 ? additionalBudget : undefined,
    targetingMode: submission.campaign?.targetingMode,
    geoTargetZips: submission.campaign?.geoTargetZips,
    paymentPlan: isMonthly ? 'monthly' : 'upfront',
  });
  const feasibilityBlocks = feasibility.verdict
    ? !feasibility.verdict.feasible
    : false;

  const canContinue =
    !!newEnd &&
    newEnd > currentEndDate &&
    !!additionalBudget &&
    additionalBudget >= cfg.minBudget &&
    !feasibility.loading &&
    !feasibilityBlocks;

  // CTV-488: "Last running creatives" — the Submission's active Creatives
  // whose [creative_start_date, creative_end_date] window includes the
  // current end_date. These are the rows the server's Blanket extension rule
  // will lengthen; the receipt step lists them so the buyer can verify what
  // will run on the extended tail. The tail share is the LAST entry of each
  // row's `segment_shares` jsonb.
  const currentEndDay = currentEndDate.slice(0, 10);
  const continuingCreatives = React.useMemo(
    () =>
      (submission.creatives ?? []).filter(
        (c) =>
          c.status === 'active'
          && c.creative_start_date <= currentEndDay
          && c.creative_end_date >= currentEndDay,
      ),
    [submission.creatives, currentEndDay],
  );

  // CTV-504: single unified prep call for monthly + upfront. The backend
  // returns clientSecret only when a Stripe charge is needed (always for
  // upfront; for monthly only when the in-progress installment was already
  // charged). Otherwise the breakdown is rendered and the buyer confirms with
  // a single Confirm button.
  const continueToReceipt = async () => {
    if (!newEnd || newEnd <= currentEndDate) {
      setError('Pick a date later than the current end.');
      return;
    }
    if (!additionalBudget || additionalBudget < cfg.minBudget) {
      setError(`Additional budget must be at least $${formatMoneySmart(cfg.minBudget)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await apiClient.post<{
        clientSecret: string | null;
        amountCents: number;
        paymentIntentId: string | null;
        newInstallments: MonthlyInstallmentPreview[];
        inPlacePreview: InPlacePreview | null;
      }>(`/submissions/${submission.id}/extend-payment-intent`, {
        additionalBudget,
        newEndDate: newEnd,
        // Fresh idempotency key per Continue click. The backend stamps a
        // non-deterministic audit id (`papertrailEventId`) into the PI
        // metadata, so reusing a key across attempts is rejected by Stripe.
        // Double-submit within a single click is gated by the `busy` flag.
        paymentIdempotencyKey: createIdempotencyKey(),
      });
      setPaymentClientSecret(data.clientSecret);
      setPaymentAmountCents(data.amountCents);
      setPaymentIntentId(data.paymentIntentId);
      setMonthlyPreview(isMonthly ? data.newInstallments : null);
      setInPlacePreview(data.inPlacePreview);
      analytics.track(EVENTS.CAMPAIGN_EXTEND_STARTED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        additional_budget: additionalBudget,
        new_end_date: newEnd,
        additional_impressions: additionalImpressions,
        amount_cents: data.amountCents,
      });
      setStep('receipt');
    } catch (e) {
      analytics.track(EVENTS.CAMPAIGN_EXTEND_FAILED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
      });
      setError(e instanceof Error ? e.message : 'Failed to start extension');
    } finally {
      setBusy(false);
    }
  };

  const backToInputs = () => {
    setStep('inputs');
    setError(null);
    setPaymentClientSecret(null);
    setPaymentIntentId(null);
    setPaymentAmountCents(0);
    setMonthlyPreview(null);
    setInPlacePreview(null);
  };

  const confirmMonthlyExtension = async () => {
    if (!newEnd || !additionalBudget) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.post(`/submissions/${submission.id}/extend-campaign`, {
        newEndDate: newEnd,
        additionalBudget,
      });
      analytics.track(EVENTS.CAMPAIGN_EXTENDED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
        additional_budget: additionalBudget,
        new_end_date: newEnd,
        amount_cents: 0,
      });
      onExtended(newEnd, additionalBudget);
    } catch (e) {
      analytics.track(EVENTS.CAMPAIGN_EXTEND_FAILED, {
        station_slug: cfg.slug,
        submission_id: submission.id,
      });
      setError(e instanceof Error ? e.message : 'Failed to confirm extension');
    } finally {
      setBusy(false);
    }
  };

  // CTV-504: shared Stripe-success handler — works for upfront AND for monthly
  // extends where the in-progress installment was already charged (an
  // on-session Elements step charges the in-progress-bucket delta first).
  // POSTs to /extend-campaign with the captured paymentIntentId so the
  // backend applies the in-place flight update + appended new flights + edits
  // row inside a single transaction.
  const onSharedPaymentSuccess = async () => {
    if (!paymentIntentId) {
      setError('Missing payment reference — please refresh and try again.');
      return;
    }
    try {
      await apiClient.post(`/submissions/${submission.id}/extend-campaign`, {
        newEndDate: newEnd,
        additionalBudget,
        paymentIntentId,
      });
    } catch {
      // The PI is already captured; surface a soft warning but still flip
      // the local UI since the user did pay successfully.
    }
    analytics.track(EVENTS.CAMPAIGN_EXTENDED, {
      station_slug: cfg.slug,
      submission_id: submission.id,
      additional_budget: additionalBudget ?? 0,
      new_end_date: newEnd,
      amount_cents: paymentAmountCents,
    });
    onExtended(newEnd, additionalBudget ?? 0);
  };

  return (
    <div>
      <p className="text-[13px] text-slate-500 mb-4">
        {step === 'receipt'
          ? `Extending through ${new Date(newEnd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`
          : `Push the end date out and add more budget to keep this commercial running on ${cfg.name}.`}
      </p>

      {step === 'receipt' && newEnd && additionalBudget && (
        <ExtensionReceiptStep
          cfg={cfg}
          newEndDate={newEnd}
          additionalBudget={additionalBudget}
          additionalFee={additionalFee}
          additionalTotal={additionalTotal}
          isMonthly={isMonthly}
          continuingCreatives={continuingCreatives}
          monthlyPreview={monthlyPreview}
          paymentClientSecret={paymentClientSecret}
          paymentAmountCents={paymentAmountCents}
          busy={busy}
          error={error}
          onBack={backToInputs}
          onCancel={onClose}
          onPaymentSuccess={onSharedPaymentSuccess}
          inPlacePreview={inPlacePreview}
          onPaymentError={(msg) => setError(msg)}
          onConfirmMonthly={() => void confirmMonthlyExtension()}
        />
      )}

      {step === 'inputs' && (
      <>
      <div className="space-y-3">
          <DateField
            id={`extend-${submission.id}`}
            label="New end date"
            required
            value={newEnd}
            onChange={setNewEnd}
            min={minNewEnd}
            accentColor={accent}
            primaryColor={cfg.primaryColor}
          />

          {/* Additional budget input — same visual language as wizard fields. */}
          <div
            className="relative px-5 pt-2 rounded-xl transition-colors group"
            style={{
              backgroundColor: tintFromAccent(accent, 0.05),
              border: `1px solid ${tintFromAccent(accent, 0.18)}`,
            }}
          >
            <span
              aria-hidden
              className="absolute left-0 top-3 bottom-3 w-[2px] rounded-r opacity-0 group-focus-within:opacity-100 transition-opacity"
              style={{ backgroundColor: accent }}
            />
            <label
              htmlFor={`extend-budget-${submission.id}`}
              className="absolute left-5 top-3 pointer-events-none select-none text-[11px] font-medium tracking-[0.08em] uppercase"
              style={{ color: accent }}
            >
              Additional budget <span aria-hidden style={{ color: accent }}>•</span>
            </label>
            <input
              id={`extend-budget-${submission.id}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={additionalBudget == null ? '' : new Intl.NumberFormat('en-US').format(additionalBudget)}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                setAdditionalBudget(digits === '' ? null : Number(digits));
              }}
              placeholder={`Minimum $${formatMoneySmart(cfg.minBudget)}`}
              className="peer w-full bg-transparent border-0 px-0 pl-4 pt-7 pb-2 text-[15px] text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-0"
              style={{ caretColor: accent }}
            />
            <span
              aria-hidden
              className="absolute left-5 top-[2.25rem] text-[15px] leading-none text-slate-500 pointer-events-none select-none opacity-0 transition-opacity peer-focus:opacity-100 peer-[:not(:placeholder-shown)]:opacity-100"
            >
              $
            </span>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl px-4 py-3" style={{ backgroundColor: `${accent}0d` }}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
            Estimated additional ad plays
          </div>
          <div className="text-[15px] font-semibold tabular-nums text-slate-900">
            {additionalImpressions > 0 ? `~${additionalImpressions.toLocaleString()}` : '—'}
          </div>
        </div>
        {!isMonthly && additionalTotal > 0 && (
          <div className="mt-1 px-1 text-[11px] text-slate-500 flex justify-between">
            <span>Total charge today</span>
            <span className="tabular-nums text-slate-700 font-medium">${formatMoneySmart(additionalTotal)}</span>
          </div>
        )}
        {isMonthly && additionalBudget && additionalBudget > 0 && (
          <p className="mt-1 px-1 text-[11px] text-slate-500">
            You'll see the full charge breakdown — anything billed today vs. on the cadence — on the next step.
          </p>
        )}

        <p className="text-[11px] text-slate-400 mt-3 px-1">
          Currently ends {new Date(currentEndDate.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
        </p>

        {/* CTV-474: inventory verdict for the extended window. Renders only
            once the buyer has picked both a later end date and a positive
            additional budget, and only when the verdict is negative. The
            extend modal can't change geo from inside itself, so we suppress
            the "widen your geo" remediation — the feasibility request already
            inherits the submission's original geo selection. */}
        {newEnd && newEnd > currentEndDate && additionalBudget && additionalBudget > 0 && (
          <div className="mt-4">
            <InventoryWarningCard
              verdict={feasibility.verdict}
              accentColor={accent}
              suppressGeoBlock
              onApplyExtensionDate={setNewEnd}
            />
          </div>
        )}

        {error && <p className="text-xs text-red-500 mt-2 px-1">{error}</p>}

        {feasibility.loading && newEnd && newEnd > currentEndDate && additionalBudget && additionalBudget > 0 && (
          <p className="mt-3 px-1 text-[11px] text-slate-500 flex items-center justify-end gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Confirming inventory before you continue…
          </p>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void continueToReceipt()}
            disabled={busy || !canContinue}
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-[13px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
              boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
            }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Continue
          </button>
        </div>
      </>
      )}
    </div>
  );
}

// CTV-488 — Extension receipt step. Single combined screen replacing the old
// creative-review + payment surfaces. Lays out charge summary, the read-only
// list of Last running creatives that will continue through the extension, a
// swap hint pointing buyers to "Run this Campaign Again" if they want a
// different rotation, and the confirm action (Stripe Elements for upfront,
// single Confirm button for monthly).
interface ExtensionReceiptStepProps {
  cfg: PublisherConfig;
  newEndDate: string;
  additionalBudget: number;
  additionalFee: number;
  additionalTotal: number;
  isMonthly: boolean;
  continuingCreatives: SubmissionRow['creatives'];
  monthlyPreview: MonthlyInstallmentPreview[] | null;
  inPlacePreview: InPlacePreview | null;
  paymentClientSecret: string | null;
  paymentAmountCents: number;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onCancel: () => void;
  onPaymentSuccess: () => void;
  onPaymentError: (msg: string) => void;
  onConfirmMonthly: () => void;
}

function ExtensionReceiptStep({
  cfg,
  newEndDate,
  additionalBudget,
  additionalFee,
  additionalTotal,
  isMonthly,
  continuingCreatives,
  monthlyPreview,
  inPlacePreview,
  paymentClientSecret,
  paymentAmountCents,
  busy,
  error,
  onBack,
  onCancel,
  onPaymentSuccess,
  onPaymentError,
  onConfirmMonthly,
}: ExtensionReceiptStepProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  const formatDate = (iso: string) =>
    new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  return (
    <div className="space-y-4">
      {/* Charge summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          {isMonthly ? 'Monthly charge summary' : 'Charge summary'}
        </div>
        {isMonthly ? (
          monthlyPreview ? (
            <>
              {/* CTV-504: "today" line for the in-progress-bucket delta when
                  the extension lands inside the current bucket. Distinct from
                  scheduled installments so the buyer can see what's about to
                  be charged via Elements vs. what runs on the cron later. */}
              {inPlacePreview && paymentAmountCents > 0 ? (
                <>
                  <p className="mt-2 text-[13px] text-slate-700">
                    Today's charge covers the rest of the current billing period through {formatDate(inPlacePreview.newEndDate)}.
                  </p>
                  <div className="mt-3 flex items-center justify-between text-[12.5px] text-slate-700">
                    <span>
                      <span className="font-medium text-slate-900">Today</span>
                      <span className="text-slate-500"> · through {formatDate(inPlacePreview.newEndDate)}</span>
                    </span>
                    <span className="tabular-nums font-medium text-slate-900">
                      ${formatMoneySmart(paymentAmountCents / 100)}
                    </span>
                  </div>
                </>
              ) : null}

              {/* CTV-502: the in-progress installment bump renders BEFORE the
                  new-installment list because the bumped installment's due date
                  comes chronologically before any appended installments. The
                  "covers delivery through" date is the bumped installment's own
                  periodEnd (e.g. Apr 30) — NOT the campaign's new end date —
                  because the next installment takes over for May+ delivery. */}
              {paymentAmountCents === 0 && inPlacePreview?.installmentBumped ? (
                // CTV-504: the in-progress installment is still `scheduled`,
                // so its amount_cents bumps in place to cover the new days.
                // Show before/after so the buyer knows what the next charge
                // will be — "no new charges today" was misleading because the
                // next cron charge is actually larger.
                <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
                  <div className="font-medium text-slate-900">
                    Your {formatDate(inPlacePreview.installmentBumped.dueDate)} installment will charge ${formatMoneySmart(inPlacePreview.installmentBumped.amountCentsAfter / 100)}.
                  </div>
                  <div className="mt-1 text-slate-500">
                    Previously ${formatMoneySmart(inPlacePreview.installmentBumped.amountCentsBefore / 100)} — the extra ${formatMoneySmart((inPlacePreview.installmentBumped.amountCentsAfter - inPlacePreview.installmentBumped.amountCentsBefore) / 100)} covers delivery through {formatDate(inPlacePreview.installmentBumped.periodEnd)}.
                  </div>
                  <div className="mt-1 text-slate-500">
                    No charge today.
                  </div>
                </div>
              ) : null}

              {monthlyPreview.length > 0 ? (
                <>
                  <p className="mt-3 text-[13px] text-slate-700">
                    {monthlyPreview.length === 1
                      ? '1 additional installment'
                      : `${monthlyPreview.length} additional installments`} will be billed automatically using the card on file.
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {monthlyPreview.map((i) => (
                      <li
                        key={i.installmentNumber}
                        className="flex items-center justify-between text-[12.5px] text-slate-700"
                      >
                        <span>
                          <span className="font-medium text-slate-900">{formatDate(i.dueDate)}</span>
                          <span className="text-slate-500"> · {formatDate(i.periodStart)} – {formatDate(i.periodEnd)}</span>
                        </span>
                        <span className="tabular-nums font-medium text-slate-900">
                          ${formatMoneySmart(i.amountCents / 100)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              {paymentAmountCents === 0 && !inPlacePreview?.installmentBumped ? (
                monthlyPreview.length === 0 ? (
                  // Pure intra-bucket on an already-paid installment (no bump,
                  // no scheduled installments ahead). Defensive — Case 3 routes
                  // through Stripe Elements so this branch is unreachable in
                  // production, but the empty-state copy lives here for safety.
                  <p className="mt-3 text-[11px] text-slate-500">
                    Today's billing period bumps to cover {formatDate(newEndDate)}.
                  </p>
                ) : (
                  <p className="mt-3 text-[11px] text-slate-500">No charge today.</p>
                )
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-[13px] text-slate-500">Calculating installments…</p>
          )
        ) : (
          <>
            <div className="mt-2 flex items-center justify-between text-[13px] text-slate-700">
              <span>Additional budget</span>
              <span className="tabular-nums">${formatMoneySmart(additionalBudget)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-[13px] text-slate-700">
              <span>Service fee</span>
              <span className="tabular-nums">${formatMoneySmart(additionalFee)}</span>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[14px] font-semibold text-slate-900">
              <span>Total charged today</span>
              <span className="tabular-nums">${formatMoneySmart(additionalTotal)}</span>
            </div>
          </>
        )}
      </div>

      {/* Continuing creatives */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2 px-1">
          Continuing through {formatDate(newEndDate)}
        </div>
        {continuingCreatives.length > 0 ? (
          <div className="space-y-2">
            {continuingCreatives.map((c) => (
              <ContinuingCreativeRow
                key={c.id}
                slug={cfg.slug}
                storagePath={c.storage_path}
                filename={c.filename}
                clickThroughUrl={c.click_through_url}
                tailShare={tailShareForCreative(c.segment_shares)}
              />
            ))}
          </div>
        ) : (
          <Banner tone="warn" label="No active commercials">
            This campaign has no active commercials covering its current end date. Add a commercial in the Edit
            commercial tab first, then come back here.
          </Banner>
        )}
      </div>

      {/* Swap hint */}
      <Banner tone="info" label="Want different commercials?" accentColor={accent}>
        Extending keeps whatever was running on the last day of your campaign going through the new end date. If
        you want a different rotation over the extended window, start a new campaign with{' '}
        <strong>Run this Campaign Again</strong> instead.
      </Banner>

      {/* Confirm action */}
      {paymentClientSecret ? (
        <>
          <StripePaymentSection
            clientSecret={paymentClientSecret}
            accentColor={accent}
            amountCents={paymentAmountCents}
            onSuccess={onPaymentSuccess}
            onError={onPaymentError}
          />
          {error && <p className="text-xs text-red-500 mt-2 px-1">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onBack}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-40"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 rounded-full text-[13px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </>
      ) : isMonthly ? (
        <>
          {error && <p className="text-xs text-red-500 px-1">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onBack}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-40"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={onConfirmMonthly}
              disabled={busy || continuingCreatives.length === 0}
              className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full text-[13px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${cfg.primaryColor})`,
                boxShadow: `0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 16px -8px ${cfg.primaryColor}`,
              }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Confirm extension
            </button>
          </div>
        </>
      ) : (
        // Upfront receipt without a clientSecret yet — defensive only;
        // continueToReceipt() always fetches one before flipping the step.
        <p className="text-[12px] text-slate-500 px-1">Preparing payment…</p>
      )}
    </div>
  );
}

// CTV-488: the Weight column on the receipt step is the share of the last
// `segment_shares` entry — that's the rotation share that will apply over
// the extended tail under the Blanket extension rule.
function tailShareForCreative(
  segmentShares: SubmissionRow['creatives'][number]['segment_shares'],
): number {
  if (!Array.isArray(segmentShares) || segmentShares.length === 0) return 100;
  return Number(segmentShares[segmentShares.length - 1]?.share ?? 100);
}

const ContinuingCreativeRow: React.FC<{
  slug: string;
  storagePath: string;
  filename: string;
  clickThroughUrl: string | null;
  tailShare: number;
}> = ({ slug, storagePath, filename, clickThroughUrl, tailShare }) => {
  const { url, loading } = useCreativePreviewUrl(slug, storagePath);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
      <div className="w-32 shrink-0">
        {loading || !url ? (
          <div className="aspect-video w-full rounded bg-slate-100 animate-pulse" />
        ) : (
          <video src={url} controls className="aspect-video w-full rounded bg-black" preload="metadata" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-slate-900 truncate" title={filename}>
          {filename}
        </div>
        {clickThroughUrl ? (
          <div className="mt-0.5 text-[11px] text-slate-500 truncate" title={clickThroughUrl}>
            {clickThroughUrl}
          </div>
        ) : null}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          Weight
        </div>
        <div className="mt-0.5 text-[14px] font-semibold tabular-nums text-slate-900">
          {tailShare}%
        </div>
      </div>
    </div>
  );
};
